import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import {
  readPersistedActiveContext,
  setActiveContext,
  type ActiveContext,
} from '@/lib/active-context-store';
import { useAccessEpoch } from '@/lib/access-epoch';
import { apiClient } from '@/lib/api-client';
import type { paths } from '@/lib/api-types';

export type EligibleOrganizations =
  paths['/v1/active-context/eligible-organizations']['get']['responses'][200]['content']['application/json'];

export type ActiveContextState =
  | { status: 'loading'; eligible: []; active: null }
  | { status: 'unprovisioned'; eligible: []; active: null }
  | { status: 'error'; eligible: []; active: null; message: string }
  | { status: 'ready'; eligible: EligibleOrganizations; active: ActiveContext | null };

export type ActiveContextValue = ActiveContextState & {
  selectContext: (context: ActiveContext) => void;
  reload: () => void;
};

const ActiveContextReactContext = createContext<ActiveContextValue | null>(null);

type Eligible = readonly { readonly id: string; readonly tenantId: string }[];

/** The preferred context when it is still eligible, else the first eligible one, else none. */
function chooseActive(data: Eligible, preferred: ActiveContext | null): ActiveContext | null {
  const stillEligible =
    preferred !== null &&
    data.some(
      (organization) =>
        organization.tenantId === preferred.tenantId &&
        organization.id === preferred.organizationId,
    );
  if (stillEligible) return preferred;
  const first = data[0];
  return first !== undefined ? { tenantId: first.tenantId, organizationId: first.id } : null;
}

function useReloadOnAccessChange(
  requestIdRef: { current: number },
  setState: Dispatch<SetStateAction<ActiveContextState>>,
) {
  const epoch = useAccessEpoch();
  const firstEpoch = useRef(epoch);
  useEffect(() => {
    if (epoch === firstEpoch.current) {
      return;
    }
    const requestId = ++requestIdRef.current;
    apiClient
      .GET('/v1/active-context/eligible-organizations')
      .then(({ data, error, response }) => {
        if (
          requestIdRef.current !== requestId ||
          error !== undefined ||
          !response.ok ||
          data === undefined
        ) {
          return;
        }
        setState((previous) => {
          if (previous.status !== 'ready') {
            return previous;
          }
          return { status: 'ready', eligible: data, active: chooseActive(data, previous.active) };
        });
      })
      .catch(() => {});
  }, [epoch]);
}

/**
 * Loads the caller's eligible (Tenant, Organization) pairs (FR-4.12, SL-0007 closure — sourced ONLY
 * from `GET /v1/active-context/eligible-organizations`, never a reverse-enumeration surface) and
 * holds the active-context SELECTION — never an authorization cache (FR-4.6/FR-6.3). `active` is
 * synced into the module-level store (`setActiveContext`) on every change so `api-client` can attach
 * the `X-Active-*` headers without importing React.
 *
 * A `401` is treated as a distinct, benign `unprovisioned` state, NOT an error: an authenticated
 * caller who was never invited (no `users` row — this MVP has no JIT provisioning) can still use
 * domain-open modules, so the shell renders a calm empty state rather than a red error. Genuine
 * failures (5xx, network) remain `error` with a retry.
 */
export function ActiveContextProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ActiveContextState>({
    status: 'loading',
    eligible: [],
    active: null,
  });
  const requestIdRef = useRef(0);

  const load = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setState({ status: 'loading', eligible: [], active: null });

    apiClient
      .GET('/v1/active-context/eligible-organizations')
      .then(({ data, error, response }) => {
        if (requestIdRef.current !== requestId) {
          return;
        }
        if (response.status === 401) {
          setState({ status: 'unprovisioned', eligible: [], active: null });
          return;
        }
        if (error !== undefined || !response.ok || data === undefined) {
          throw new Error(
            `core-api returned ${response.status} for /v1/active-context/eligible-organizations`,
          );
        }

        const active = chooseActive(data, readPersistedActiveContext());

        setState({ status: 'ready', eligible: data, active });
      })
      .catch((error: unknown) => {
        if (requestIdRef.current !== requestId) {
          return;
        }
        setState({
          status: 'error',
          eligible: [],
          active: null,
          message: error instanceof Error ? error.message : 'Errore',
        });
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Decision 151: after a change of access, read the eligible organizations again WITHOUT passing
  // through «loading» (which would empty the shell): the chosen organization stays if it is still
  // eligible, and a failed re-read leaves everything as it was.
  useReloadOnAccessChange(requestIdRef, setState);

  // Skip the initial `loading` render: its `active` is always `null` before the eligible-list
  // fetch resolves, and syncing that to the store would clear a not-yet-verified persisted
  // context out from under `readPersistedActiveContext()` above.
  useEffect(() => {
    if (state.status === 'loading') {
      return;
    }
    setActiveContext(state.active);
  }, [state.status, state.active]);

  const selectContext = (context: ActiveContext) => {
    setState((previous) =>
      previous.status === 'ready' ? { ...previous, active: context } : previous,
    );
  };

  return (
    <ActiveContextReactContext.Provider value={{ ...state, selectContext, reload: load }}>
      {children}
    </ActiveContextReactContext.Provider>
  );
}

export function useActiveContext(): ActiveContextValue {
  const context = useContext(ActiveContextReactContext);
  if (!context) {
    throw new Error('useActiveContext must be used within an ActiveContextProvider');
  }
  return context;
}

/**
 * The same context, but ABSENT rather than fatal when no provider is mounted.
 *
 * WHY BOTH EXIST, because two hooks for one context needs justifying. The throwing version is the
 * right default: a screen that needs the active organization and does not have one is a wiring
 * mistake, and failing loudly at the first render is how that gets found. This version is for the
 * narrow case where the context REFINES behaviour rather than enabling it — a read that re-runs
 * when the organization changes, and is simply not re-run when nothing can tell it that it did.
 *
 * Added 2026-09-14 for `useCallerPermissions`, which became organization-aware so a navigation
 * entry could depend on it. Making it throw would have turned eleven passing page tests red for a
 * dependency those pages do not have — and rewriting eleven tests to mount a provider they never
 * needed is a worse trade than one honest extra hook.
 */
export function useActiveContextOptional(): ActiveContextValue | undefined {
  return useContext(ActiveContextReactContext) ?? undefined;
}
